import React, { useRef, useState } from "react";
import { analyzeText, buildReport, IntelligenceReport } from "../data/intelligenceEngine";

interface StoredReport {
  id: string;
  residentName: string;
  createdAt: string;
  source: string;
  report: IntelligenceReport;
}

const REPORTS_KEY = "intelligence_reports";

function loadReports(): StoredReport[] {
  try {
    return JSON.parse(localStorage.getItem(REPORTS_KEY) || "[]");
  } catch {
    return [];
  }
}

export default function Intelligence({ user }: { user?: any }) {
  const [tab, setTab] = useState<"analyze" | "advisor">("analyze");
  const [text, setText] = useState("");
  const [residentName, setResidentName] = useState("");
  const [source, setSource] = useState("");
  const [report, setReport] = useState<IntelligenceReport | null>(null);
  const [reports, setReports] = useState<StoredReport[]>(loadReports);
  const [message, setMessage] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const runAnalysis = () => {
    if (!text.trim()) {
      setMessage("Paste assessment text or upload a file first.");
      return;
    }

    const detections = analyzeText(text);

    if (detections.length === 0) {
      setReport(null);
      setMessage("No clinical indicators detected yet. Add more assessment text (hospital, GP, OT, SLT reports).");
      return;
    }

    setReport(buildReport(detections));
    setMessage(`Detected ${detections.length} clinical indicator(s). Review the generated plan below.`);
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    files.forEach(file => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = String(e.target?.result || "");

        setText(current => (current ? current + "\n" : "") + content);
        setSource(file.name);
      };
    });

    if (files.length) setMessage(`Loaded ${files.length} file(s). Add more or run the analysis.`);
  };

  const saveReport = () => {
    if (!report) {
      setMessage("Run an analysis first.");
      return;
    }

    const stored: StoredReport = {
      id: `ir-${Date.now()}`,
      residentName: residentName || "Unassigned",
      createdAt: new Date().toISOString(),
      source: source || "Pasted text",
      report,
    };

    const next = [stored, ...reports];

    setReports(next);
    localStorage.setItem(REPORTS_KEY, JSON.stringify(next));
    setMessage(`Intelligence report saved for ${stored.residentName}.`);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">AI Intelligence Engine</h2>
        <p className="text-sm text-slate-600">Upload or paste any assessment (hospital, GP, OT, SLT, social work) and the engine generates the DoLS narrative, dietary needs, care needs, equipment, referrals, monitoring plan and a dependency-based fee.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab("analyze")} className={tab === "analyze" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Analyze Assessments</button>
        <button onClick={() => setTab("advisor")} className={tab === "advisor" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Advisor ({reports.length})</button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "analyze" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Resident name (optional)</label>
                <input value={residentName} onChange={event => setResidentName(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Source documents (.txt / .csv / .md)</label>
                <input ref={fileRef} type="file" accept=".txt,.csv,.md,.text" multiple onChange={handleFile} className="w-full text-sm" />
                {source ? <div className="mt-1 text-xs text-slate-500">Loaded: {source}</div> : null}
              </div>
            </div>
            <label className="mb-1 mt-3 block text-xs font-medium text-slate-600">Assessment text (paste hospital / GP / OT / SLT reports)</label>
            <textarea value={text} onChange={event => setText(event.target.value)} rows={8} placeholder="Paste all assessment text here. The engine reads every document and builds the full picture." className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-3 flex gap-2">
              <button onClick={runAnalysis} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Run AI Analysis</button>
              <button onClick={saveReport} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save Report</button>
            </div>
          </div>

          {report ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-slate-900">Detected conditions</h3>
                  <div className="text-sm font-semibold text-slate-900">Dependency: {report.band} | Suggested fee £{report.suggestedMonthlyFee.toLocaleString()}/month</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {report.detections.map(d => (
                    <span key={d.id} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">{d.condition} ({d.confidence}%)</span>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Care needs</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{report.careNeeds.map(item => <li key={item}>{item}</li>)}</ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Dietary requirements</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{report.dietary.length ? report.dietary.map(item => <li key={item}>{item}</li>) : <li>No specific dietary indicators.</li>}</ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Equipment</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{report.equipment.length ? report.equipment.map(item => <li key={item}>{item}</li>) : <li>No specific equipment indicated.</li>}</ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Referrals</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{report.referrals.length ? report.referrals.map(item => <li key={item}>{item}</li>) : <li>No referrals indicated.</li>}</ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Monitoring plan</h4>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{report.monitoring.map(item => <li key={item}>{item}</li>)}</ul>
                </div>
              </div>

              <div className="rounded-xl border-2 border-[#123B36] bg-white p-4 shadow-sm">
                <h4 className="mb-2 text-sm font-semibold text-[#123B36]">MCA / DoLS narrative (auto-generated)</h4>
                <p className="text-sm leading-6 text-slate-700">{report.dolsNarrative}</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">{report.mcaDols.map(item => <li key={item}>{item}</li>)}</ul>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "advisor" ? (
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">No intelligence reports yet. Run an analysis and save it to see live advice here.</div>
          ) : null}

          {reports.map(stored => (
            <div key={stored.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{stored.residentName}</div>
                <div className="text-xs text-slate-500">{new Date(stored.createdAt).toLocaleDateString()} | {stored.source}</div>
              </div>
              <div className="mt-1 text-sm text-slate-700">Dependency {stored.report.band} | Suggested fee £{stored.report.suggestedMonthlyFee.toLocaleString()}/month</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {stored.report.detections.slice(0, 6).map(d => (
                  <span key={d.id} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">{d.condition}</span>
                ))}
              </div>
              <div className="mt-3 rounded-lg bg-slate-50 p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Advisor actions</div>
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                  {stored.report.mcaDols.length ? <li>MCA/DoLS: {stored.report.mcaDols[0]}</li> : null}
                  {stored.report.monitoring.slice(0, 3).map(item => <li key={item}>{item}</li>)}
                  {stored.report.referrals.slice(0, 3).map(item => <li key={item}>Refer: {item}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}